// Force-directed vault graph on a canvas (d3-force + manual hit testing).
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type Simulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from 'd3-force';
import { h } from './dom.js';
import type { GraphData } from './types.js';

interface GraphNode extends SimulationNodeDatum {
  id: string;
  title: string;
  tags: string[];
}

interface GraphLink extends SimulationLinkDatum<GraphNode> {
  source: string | GraphNode;
  target: string | GraphNode;
}

interface Transform {
  x: number;
  y: number;
  k: number;
}

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 6;

export class GraphView {
  readonly el: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly tooltip: HTMLElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly empty: HTMLElement;
  private simulation: Simulation<GraphNode, GraphLink>;
  private nodes: GraphNode[] = [];
  private links: GraphLink[] = [];
  private transform: Transform = { x: 0, y: 0, k: 1 };
  private hovered: GraphNode | null = null;
  private dragging: GraphNode | null = null;
  private dragStart: { x: number; y: number } | null = null;
  private panning: { x: number; y: number } | null = null;
  private needsDraw = false;
  private userAdjusted = false;
  private frame = 0;
  private width = 0;
  private height = 0;
  private colors = { fg: '#888', muted: '#aaa', accent: '#7c3aed', bg: 'transparent', border: '#ddd' };
  private readonly onOpen: (path: string) => void;
  private readonly resizeObserver: ResizeObserver;

  constructor(onOpen: (path: string) => void) {
    this.onOpen = onOpen;
    this.canvas = h('canvas', { class: 'graph-canvas' });
    this.tooltip = h('div', { class: 'graph-tooltip', attrs: { hidden: 'hidden' } });
    this.empty = h('p', { class: 'empty-note graph-empty', text: 'No notes in the vault yet' });
    this.el = h('div', { class: 'graph-host' }, [this.canvas, this.tooltip, this.empty]);
    const context = this.canvas.getContext('2d');
    if (!context) throw new Error('canvas 2d context unavailable');
    this.context = context;
    this.simulation = forceSimulation<GraphNode>([])
      .force('charge', forceManyBody<GraphNode>().strength(-120))
      .force('center', forceCenter(0, 0))
      .force('collide', forceCollide<GraphNode>(12))
      .on('tick', () => this.requestDraw());
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.el);
    this.canvas.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.canvas.addEventListener('pointermove', (event) => this.onPointerMove(event));
    this.canvas.addEventListener('pointerup', (event) => this.onPointerUp(event));
    this.canvas.addEventListener('pointerleave', () => this.onPointerLeave());
    this.canvas.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
    this.resize();
  }

  /** Reads theme colors so the canvas matches the CSS variables. */
  refreshTheme(): void {
    const style = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string): string => style.getPropertyValue(name).trim() || fallback;
    this.colors = {
      fg: read('--fg', '#1f2328'),
      muted: read('--muted', '#6b7280'),
      accent: read('--accent', '#7c3aed'),
      bg: 'transparent',
      border: read('--border', '#e5e7eb'),
    };
    this.requestDraw();
  }

  setData(data: GraphData): void {
    const previous = new Map(this.nodes.map((node) => [node.id, node]));
    this.nodes = data.nodes.map((node) => {
      const old = previous.get(node.path);
      const datum: GraphNode = {
        id: node.path,
        title: node.title,
        tags: node.tags,
        ...(old?.x !== undefined ? { x: old.x, y: old.y, vx: old.vx, vy: old.vy } : {}),
      };
      return datum;
    });
    // Seed new nodes on a circle so the first paint is already inside the canvas
    // (the canvas may be hidden while another sidebar tab is active).
    const centerX = this.width / 2;
    const centerY = this.height / 2;
    const radius = Math.max(40, Math.min(this.width, this.height) / 3);
    this.nodes.forEach((node, index) => {
      if (node.x !== undefined) return;
      const angle = (index / Math.max(1, this.nodes.length)) * Math.PI * 2;
      node.x = centerX + radius * Math.cos(angle);
      node.y = centerY + radius * Math.sin(angle);
    });
    this.links = data.edges.map((edge) => ({ source: edge.source, target: edge.target }));
    this.empty.hidden = this.nodes.length > 0;
    this.simulation.nodes(this.nodes);
    this.simulation.force(
      'link',
      forceLink<GraphNode, GraphLink>(this.links)
        .id((node) => node.id)
        .distance(60)
        .strength(0.4),
    );
    this.simulation.force('center', forceCenter(centerX, centerY));
    this.simulation.alpha(this.nodes.length > 400 ? 0.6 : 1).restart();
    if (!this.userAdjusted) this.fit();
  }

  /**
   * Centers the layout in the canvas; skipped once the user has zoomed or
   * panned by hand, and while the pane has no real size.
   */
  private fit(): void {
    if (this.nodes.length === 0 || this.width < 40 || this.height < 40) return;
    if (this.userAdjusted) return;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const node of this.nodes) {
      minX = Math.min(minX, node.x ?? 0);
      maxX = Math.max(maxX, node.x ?? 0);
      minY = Math.min(minY, node.y ?? 0);
      maxY = Math.max(maxY, node.y ?? 0);
    }
    const spanX = Math.max(maxX - minX, 40);
    const spanY = Math.max(maxY - minY, 40);
    const k = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min(this.width / (spanX + 80), this.height / (spanY + 80))));
    this.transform = {
      k,
      x: this.width / 2 - ((minX + maxX) / 2) * k,
      y: this.height / 2 - ((minY + maxY) / 2) * k,
    };
    this.simulation.force('center', forceCenter(this.width / 2, this.height / 2));
    this.requestDraw();
  }

  private resize(): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const previousWidth = this.width;
    const previousHeight = this.height;
    const ratio = window.devicePixelRatio || 1;
    this.width = parent.clientWidth;
    this.height = Math.max(120, parent.clientHeight);
    this.canvas.width = Math.max(1, Math.floor(this.width * ratio));
    this.canvas.height = Math.max(1, Math.floor(this.height * ratio));
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
    if (previousWidth !== this.width || previousHeight !== this.height) this.fit();
    this.requestDraw();
  }

  private requestDraw(): void {
    this.needsDraw = true;
    if (this.frame !== 0) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = 0;
      if (this.needsDraw) this.draw();
    });
  }

  private screen(node: GraphNode): { x: number; y: number } {
    return { x: (node.x ?? 0) * this.transform.k + this.transform.x, y: (node.y ?? 0) * this.transform.k + this.transform.y };
  }

  private nodeAt(px: number, py: number): GraphNode | null {
    let best: GraphNode | null = null;
    let bestDistance = 14;
    for (const node of this.nodes) {
      const point = this.screen(node);
      const distance = Math.hypot(point.x - px, point.y - py);
      if (distance <= bestDistance) {
        bestDistance = distance;
        best = node;
      }
    }
    return best;
  }

  private localPoint(event: PointerEvent | WheelEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  private onPointerDown(event: PointerEvent): void {
    const point = this.localPoint(event);
    const node = this.nodeAt(point.x, point.y);
    if (node) {
      this.dragging = node;
      this.dragStart = point;
      node.fx = node.x;
      node.fy = node.y;
      this.simulation.alphaTarget(0.2).restart();
    } else {
      this.panning = point;
    }
    this.canvas.setPointerCapture(event.pointerId);
  }

  private onPointerMove(event: PointerEvent): void {
    const point = this.localPoint(event);
    if (this.dragging) {
      const node = this.dragging;
      node.fx = (point.x - this.transform.x) / this.transform.k;
      node.fy = (point.y - this.transform.y) / this.transform.k;
      this.requestDraw();
      return;
    }
    if (this.panning) {
      this.userAdjusted = true;
      this.transform.x += point.x - this.panning.x;
      this.transform.y += point.y - this.panning.y;
      this.panning = point;
      this.requestDraw();
      return;
    }
    const hovered = this.nodeAt(point.x, point.y);
    if (hovered !== this.hovered) {
      this.hovered = hovered;
      this.tooltip.hidden = hovered === null;
      if (hovered) this.tooltip.textContent = hovered.title;
      this.requestDraw();
    }
    if (hovered) {
      this.tooltip.style.left = `${point.x + 12}px`;
      this.tooltip.style.top = `${point.y - 6}px`;
    }
    this.canvas.style.cursor = hovered ? 'pointer' : 'grab';
  }

  private onPointerUp(event: PointerEvent): void {
    const point = this.localPoint(event);
    const node = this.nodeAt(point.x, point.y);
    if (this.dragging) {
      const start = this.dragStart;
      const travelled = start ? Math.hypot(point.x - start.x, point.y - start.y) : 99;
      const dragged = this.dragging;
      this.simulation.alphaTarget(0);
      dragged.fx = null;
      dragged.fy = null;
      this.dragging = null;
      this.dragStart = null;
      if (node && node === dragged && travelled < 4) this.onOpen(node.id);
    }
    this.panning = null;
    this.canvas.releasePointerCapture(event.pointerId);
  }

  private onPointerLeave(): void {
    this.hovered = null;
    this.tooltip.hidden = true;
    this.requestDraw();
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    this.userAdjusted = true;
    const point = this.localPoint(event);
    const factor = Math.exp(-event.deltaY * 0.0015);
    const k = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.transform.k * factor));
    const scale = k / this.transform.k;
    this.transform = {
      k,
      x: point.x - (point.x - this.transform.x) * scale,
      y: point.y - (point.y - this.transform.y) * scale,
    };
    this.requestDraw();
  }

  private draw(): void {
    this.needsDraw = false;
    const ctx = this.context;
    ctx.clearRect(0, 0, this.width, this.height);
    ctx.save();
    ctx.translate(this.transform.x, this.transform.y);
    ctx.scale(this.transform.k, this.transform.k);
    ctx.strokeStyle = this.colors.border;
    ctx.globalAlpha = 0.75;
    ctx.lineWidth = 1 / this.transform.k;
    ctx.beginPath();
    for (const link of this.links) {
      const source = link.source as GraphNode;
      const target = link.target as GraphNode;
      if (typeof source === 'string' || typeof target === 'string') continue;
      ctx.moveTo(source.x ?? 0, source.y ?? 0);
      ctx.lineTo(target.x ?? 0, target.y ?? 0);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
    const radius = Math.max(3, Math.min(9, 5 / Math.sqrt(this.transform.k)));
    for (const node of this.nodes) {
      const active = node === this.hovered;
      ctx.beginPath();
      ctx.fillStyle = active || node.tags.length > 0 ? this.colors.accent : this.colors.muted;
      ctx.globalAlpha = active ? 1 : node.tags.length > 0 ? 0.9 : 0.7;
      ctx.arc(node.x ?? 0, node.y ?? 0, active ? radius * 1.6 : radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    if (this.transform.k > 1.1) {
      ctx.fillStyle = this.colors.fg;
      ctx.font = `${Math.min(14, 10 / this.transform.k)}px system-ui, sans-serif`;
      for (const node of this.nodes) {
        ctx.globalAlpha = node === this.hovered ? 1 : 0.65;
        ctx.fillText(node.title, (node.x ?? 0) + radius + 2, (node.y ?? 0) + 3);
      }
      ctx.globalAlpha = 1;
    }
    ctx.restore();
  }

  destroy(): void {
    this.resizeObserver.disconnect();
    this.simulation.stop();
    if (this.frame !== 0) window.cancelAnimationFrame(this.frame);
    this.canvas.remove();
    this.tooltip.remove();
    this.empty.remove();
  }
}
